import React, { useState, useEffect, useRef } from "react";
import { FaVideo, FaCamera, FaTimes, FaSync, FaStop, FaUpload, FaSpinner } from "react-icons/fa";
import { successAlert, errorAlert, warningAlert } from "../utils/alert";
import API from "../api/api";

const CameraCaptureModal = ({ onClose, onSuccess, initialDestination = "gallery" }) => {
  const [mode, setMode] = useState("photo"); // "photo" or "video"
  const [destination, setDestination] = useState(initialDestination); // "gallery", "gallery_player", "news"
  const [title, setTitle] = useState("");
  const [uploadDate, setUploadDate] = useState("");
  
  // Camera state
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [stream, setStream] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState("");

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState("");

  // Photo state
  const [capturedPhoto, setCapturedPhoto] = useState(null); // Blob
  const [photoUrl, setPhotoUrl] = useState("");

  // UI state
  const [uploading, setUploading] = useState(false);

  // Refs
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Initialize and list cameras
  useEffect(() => {
    const listDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true }); // Request permission first
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = allDevices.filter(d => d.kind === "videoinput");
        setDevices(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedDeviceId(videoDevices[0].deviceId);
        }
      } catch (err) {
        console.error("Error accessing camera devices:", err);
        setCameraError("Camera permission denied or no camera device found.");
      }
    };
    listDevices();
  }, []);

  // Handle stream startup
  useEffect(() => {
    if (!selectedDeviceId) return;
    
    let activeStream = null;
    const startStream = async () => {
      try {
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        setCameraError("");
        setIsCameraActive(false);

        const constraints = {
          video: { deviceId: { exact: selectedDeviceId } },
          audio: mode === "video" // Only request audio if recording video
        };
        
        activeStream = await navigator.mediaDevices.getUserMedia(constraints);
        setStream(activeStream);
        if (videoRef.current) {
          videoRef.current.srcObject = activeStream;
        }
        setIsCameraActive(true);
      } catch (err) {
        console.error("Error starting camera stream:", err);
        setCameraError("Could not start camera stream. Ensure camera isn't in use by another app.");
      }
    };

    startStream();

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId, mode]);

  // Recording Timer Effect
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  const flipCamera = () => {
    if (devices.length < 2) return;
    const currentIndex = devices.findIndex(d => d.deviceId === selectedDeviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    setSelectedDeviceId(devices[nextIndex].deviceId);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !isCameraActive) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    
    // Draw mirrored video if front camera, or regular
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedPhoto(blob);
        setPhotoUrl(URL.createObjectURL(blob));
        // Stop stream
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setIsCameraActive(false);
        }
      }
    }, "image/jpeg", 0.9);
  };

  const startRecording = () => {
    if (!stream || !isCameraActive) return;
    recordedChunksRef.current = [];
    setRecordedBlob(null);
    setRecordedUrl("");
    setRecordingSeconds(0);

    try {
      const options = { mimeType: "video/webm;codecs=vp9" };
      let recorder;
      try {
        recorder = new MediaRecorder(stream, options);
      } catch (e) {
        recorder = new MediaRecorder(stream);
      }

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        
        // Stop stream tracks
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setIsCameraActive(false);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start(10); // Capture in chunks
      setIsRecording(true);
    } catch (err) {
      console.error("Recording start failed:", err);
      errorAlert("Error", "Could not start recording.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const resetCapture = () => {
    setCapturedPhoto(null);
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl("");
    
    setRecordedBlob(null);
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl("");
    setRecordingSeconds(0);

    // Restart camera stream
    setSelectedDeviceId(prev => prev); // Trigger reload effect
  };

  const formatTime = (secs) => {
    const minutes = String(Math.floor(secs / 60)).padStart(2, "0");
    const seconds = String(secs % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleUpload = async () => {
    const isPhoto = mode === "photo";
    const fileBlob = isPhoto ? capturedPhoto : recordedBlob;

    if (!fileBlob) {
      warningAlert("No media", "Please capture a photo or record a video first.");
      return;
    }

    const titleText = title.trim();
    if (destination === "news" && !titleText) {
      warningAlert("Title Required", "A heading/title is required for News uploads.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    const extension = isPhoto ? "jpg" : "webm";
    const filename = `camera_${Date.now()}.${extension}`;
    const mimeType = isPhoto ? "image/jpeg" : "video/webm";
    const file = new File([fileBlob], filename, { type: mimeType });

    if (destination === "news") {
      formData.append("heading", titleText || `News Clip ${new Date().toLocaleDateString()}`);
      formData.append("images[]", file);
      if (uploadDate) formData.append("date", uploadDate);
    } else {
      formData.append("files[]", file);
      formData.append("category", destination === "gallery_player" ? "player_videos" : "general");
      if (titleText) formData.append("title", titleText);
      if (uploadDate) formData.append("date", uploadDate);
    }

    try {
      if (destination === "news") {
        await API.createNewsClip(formData);
      } else {
        await API.post("/admin/gallery/upload", formData);
      }
      successAlert("Success", "Media uploaded directly successfully!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Direct camera upload failed:", err);
      errorAlert("Upload Failed", err?.message || "Something went wrong during upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-3xl flex-col rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 bg-slate-50/50">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              📸 Camera Hub
            </h3>
            <p className="text-xs text-slate-500">Capture photo or video and upload directly</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 p-6 grid grid-cols-1 md:grid-cols-2 gap-6 overflow-y-auto max-h-[70vh]">
          
          {/* Left Column: Camera Viewport / Preview */}
          <div className="flex flex-col gap-4">
            
            {/* Mode Selector (Only allow photo for news destination) */}
            {destination !== "news" && !photoUrl && !recordedUrl && (
              <div className="flex bg-slate-100 p-1 rounded-xl w-full justify-center">
                <button
                  type="button"
                  onClick={() => setMode("photo")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all ${
                    mode === "photo" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600"
                  }`}
                >
                  <FaCamera /> Photo
                </button>
                <button
                  type="button"
                  onClick={() => setMode("video")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all ${
                    mode === "video" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600"
                  }`}
                >
                  <FaVideo /> Video
                </button>
              </div>
            )}

            {/* Viewport Box */}
            <div className="relative aspect-video rounded-2xl bg-slate-900 border border-slate-200 overflow-hidden shadow-inner flex items-center justify-center">
              
              {/* Active Stream */}
              {!photoUrl && !recordedUrl && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full h-full object-cover ${selectedDeviceId ? "" : "hidden"}`}
                />
              )}

              {/* Photo Preview */}
              {photoUrl && (
                <img src={photoUrl} alt="Captured" className="w-full h-full object-cover" />
              )}

              {/* Video Preview */}
              {recordedUrl && (
                <video src={recordedUrl} controls className="w-full h-full object-cover" />
              )}

              {/* Camera Error Message */}
              {cameraError && (
                <div className="absolute inset-0 bg-slate-950/80 flex items-center justify-center p-4 text-center">
                  <p className="text-sm font-semibold text-red-400">{cameraError}</p>
                </div>
              )}

              {/* Recording Overlay UI */}
              {isRecording && (
                <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 animate-pulse">
                  <span className="w-2 h-2 rounded-full bg-white block"></span>
                  REC {formatTime(recordingSeconds)}
                </div>
              )}
            </div>

            {/* Controls Bar */}
            <div className="flex justify-center items-center gap-3">
              {!photoUrl && !recordedUrl ? (
                <>
                  {/* Capture/Record Button */}
                  {mode === "photo" ? (
                    <button
                      type="button"
                      onClick={capturePhoto}
                      disabled={!isCameraActive}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-bold px-6 py-2.5 rounded-xl text-sm shadow transition-all hover:scale-[1.02]"
                    >
                      <FaCamera /> Capture Photo
                    </button>
                  ) : (
                    <>
                      {!isRecording ? (
                        <button
                          type="button"
                          onClick={startRecording}
                          disabled={!isCameraActive}
                          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-bold px-6 py-2.5 rounded-xl text-sm shadow transition-all hover:scale-[1.02]"
                        >
                          <FaVideo /> Start Recording
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={stopRecording}
                          className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-bold px-6 py-2.5 rounded-xl text-sm shadow transition-all animate-pulse"
                        >
                          <FaStop /> Stop Recording
                        </button>
                      )}
                    </>
                  )}

                  {/* Camera Flip Button */}
                  {devices.length > 1 && (
                    <button
                      type="button"
                      onClick={flipCamera}
                      title="Flip Camera"
                      className="rounded-xl border border-slate-200 bg-white p-3 text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
                    >
                      <FaSync />
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={resetCapture}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold px-6 py-2.5 rounded-xl text-sm transition-colors"
                >
                  🔄 Retake / Record Again
                </button>
              )}
            </div>

          </div>

          {/* Right Column: Upload Metadata Settings */}
          <div className="flex flex-col gap-4 justify-between border-t md:border-t-0 md:border-l border-slate-100 md:pl-6 pt-4 md:pt-0">
            <div className="space-y-4">
              
              {/* Destination */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Destination</label>
                <select
                  value={destination}
                  onChange={(e) => {
                    setDestination(e.target.value);
                    if (e.target.value === "news") {
                      setMode("photo"); // Force photo for news
                    }
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                >
                  <option value="gallery">Gallery (Photos/Videos)</option>
                  <option value="gallery_player">Gallery (Player Videos)</option>
                  <option value="news">News Clips (Images Only)</option>
                </select>
              </div>

              {/* Title / Heading */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                  {destination === "news" ? "Heading (Required) *" : "Title (Optional)"}
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={destination === "news" ? "Enter news headline..." : "Enter media title..."}
                  required={destination === "news"}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 placeholder-slate-400 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Date (Optional)</label>
                <input
                  type="date"
                  value={uploadDate}
                  onChange={(e) => setUploadDate(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>

            </div>

            {/* Submit Action Button */}
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || (!capturedPhoto && !recordedBlob)}
              className="mt-6 w-full flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 font-bold text-white shadow-lg shadow-emerald-500/10 hover:bg-emerald-700 hover:shadow-emerald-500/20 py-3.5 px-4 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none transition-all"
            >
              {uploading ? (
                <>
                  <FaSpinner className="animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <FaUpload /> Upload Directly
                </>
              )}
            </button>
          </div>

        </div>

      </div>
    </div>
  );
};

export default CameraCaptureModal;
